import CountdownTimer from "./CountdownTimer";
import { CloudLightning, Globe, Server, HardDrive, Network, Shield, Archive, Activity, Route } from "lucide-react";

const awsModules = [
  {
    module: 1,
    title: "AWS Global Infra",
    description: "Region = DC · AZ = isolated rack row · Edge = CDN PoP",
    icon: Globe,
  },
  {
    module: 2,
    title: "EC2 Compute Flow",
    description: "Internet → IGW → ALB → ASG EC2 fleet → EBS block storage",
    icon: Server,
  },
  {
    module: 3,
    title: "Storage Hierarchy",
    description: "EBS (SAN) + EFS (NAS) + S3 (Object) + S3 Glacier (Tape)",
    icon: HardDrive,
  },
  {
    module: 4,
    title: "VPC Network Stack",
    description: "Internet → IGW → Public subnet → Private subnet → Isolated DB tier",
    icon: Network,
  },
  {
    module: 5,
    title: "Defense-in-Depth",
    description: "WAF → VPC/SG → IAM → KMS Encryption — 5 layers always active",
    icon: Shield,
  },
  {
    module: 6,
    title: "Backup & DR",
    description: "AWS Backup GFS → S3 Glacier offsite → DRS continuous replication",
    icon: Archive,
  },
  {
    module: 7,
    title: "Observability",
    description: "Metrics → Alarms → SNS → Auto-remediate or page on-call",
    icon: Activity,
  },
  {
    module: 8,
    title: "Migration Path",
    description: "Discover → Assess → Mobilize → Migrate → Operate → Optimize",
    icon: Route,
  },
];

export default function AWSCourseSection() {
  return (
    <section id="aws-course" data-testid="aws-course-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111118]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <CloudLightning size={24} className="text-[#FF9900]" />
          <p className="font-mono text-xs text-[#FF9900] uppercase tracking-widest">// course 01</p>
        </div>
        <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#F4F4F5] mb-3">
          AWS Cloud Infrastructure
        </h2>
        <p className="text-base text-[#A1A1AA] max-w-2xl mb-10">
          Every architectural pattern learned maps directly to on-premise skills you already have. Theory with Architectural Flow.
        </p>

        {/* Details + Countdown row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          <div className="p-6 bg-[#16161F] border border-[#27272A] rounded-md">
            <h3 className="font-['Space_Grotesk'] text-sm font-semibold text-[#FF9900] uppercase tracking-wider mb-4">Course Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Duration</span>
                <span>65 Hours</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Schedule</span>
                <span>Monday to Thursday</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Start Date</span>
                <span className="text-[#FF9900] font-semibold">15-June-2026</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Time</span>
                <span>05:00 PM - 06:15 PM IST</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Modules</span>
                <span>8 Modules</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span className="text-[#71717A]">Contact</span>
                <span className="text-[#22D3EE]">+91-8978266997</span>
              </div>
            </div>
          </div>

          <CountdownTimer targetDate="2026-06-15T17:00:00+05:30" label="AWS Course Starts In" accentColor="#FF9900" />
        </div>

        {/* Syllabus bento grid */}
        <h3 className="font-['Space_Grotesk'] text-lg font-semibold text-[#F4F4F5] mb-4">Syllabus — Architecture Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A] rounded-md overflow-hidden">
          {awsModules.map((mod) => {
            const Icon = mod.icon;
            return (
              <div
                key={mod.module}
                data-testid={`aws-module-${mod.module}`}
                className="p-5 bg-[#16161F] hover:bg-[#1C1C26] transition-all duration-300 group cursor-default"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-[#FF9900]/10 border border-[#FF9900]/20 group-hover:border-[#FF9900]/50 transition-all">
                    <Icon size={16} className="text-[#FF9900]" />
                  </div>
                  <span className="text-[10px] font-mono text-[#FF9900] uppercase tracking-wider">M{mod.module}</span>
                </div>
                <h4 className="font-['Space_Grotesk'] text-sm font-semibold text-[#F4F4F5] mb-1 leading-tight">
                  {mod.title}
                </h4>
                <p className="text-xs text-[#71717A] leading-relaxed">
                  {mod.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
